import type { CareerId, SkillId } from './ids';

export type PortfolioSource = 'scenario' | 'lab' | 'project' | 'interview' | 'external';

/**
 * Where a piece of evidence comes from. WorkSim only ever produces `simulation` and `lab`; the
 * learner may add `academic`, `personal` or `employment` evidence of their own, which is labeled
 * self-reported and never counts toward mastery. Employment is never inferred.
 */
export type EvidenceOrigin = 'simulation' | 'lab' | 'academic' | 'personal' | 'employment';
export const EVIDENCE_ORIGINS: readonly EvidenceOrigin[] = ['simulation', 'lab', 'academic', 'personal', 'employment'];

/** The origins a learner may choose for evidence they add themselves. */
export type ExternalEvidenceOrigin = 'academic' | 'personal' | 'employment';
export const EXTERNAL_EVIDENCE_ORIGINS: readonly ExternalEvidenceOrigin[] = ['academic', 'personal', 'employment'];

/** Evidence the learner adds from outside WorkSim, in their own words. It carries no score. */
export interface ExternalEvidence {
  id: string;
  careerId: CareerId;
  origin: ExternalEvidenceOrigin;
  title: string;
  /** The learner's own description, at most 1,000 characters. */
  summary: string;
  /** Skills the learner says the work used. Listed without a score. */
  skillIds?: SkillId[];
  /** Optional https link the learner supplies (a repository, a paper). Not checked by WorkSim. */
  link?: string;
  completedAt: string;
}

/** The fixed disclaimer on every self-reported item. */
export const SELF_REPORTED_DISCLAIMER = 'Self-reported by the learner; not produced or verified in WorkSim.';

/**
 * A portfolio item is composed from work the learner actually completed. Every field traces to a
 * record (session, deliverable, lab report, project report); nothing here is written freely.
 */
export interface PortfolioItem {
  id: string;
  source: PortfolioSource;
  sourceId: string;
  careerId: CareerId;
  title: string;
  problem: string;
  approach: string;
  tools: string[];
  responsibilities: string[];
  deliverables: { title: string; type: string; excerpt: string; ref: string }[];
  outcome: string;
  skills: { id: SkillId; name: string; score?: number }[];
  evidence: { label: string; ref: string }[];
  limitations: string[];
  /** Always present for simulated work. */
  disclaimer: string;
  completedAt: string;
  /** Learner-chosen visibility for reviewers. */
  included: boolean;
  /** True when the item came from a track's capstone project. */
  capstone?: boolean;
  /** `simulation` or `lab` for everything WorkSim produced; the learner's choice for external evidence. */
  origin: EvidenceOrigin;
  /** Set on external evidence: the learner's own account, never verified and never scored. */
  selfReported?: true;
}

export interface ResumeBullet {
  id: string;
  portfolioItemId: string;
  /** Draft generated from evidence. */
  draft: string;
  /** Learner's edited version, when changed. */
  text: string;
  /** Category the bullet belongs under on a resume. */
  section: 'projects' | 'labs' | 'education' | 'certification-preparation';
  warnings: string[];
  updatedAt: string;
}

export const PORTFOLIO_DISCLAIMER = 'Simulated work completed in WorkSim AI at the fictional company TechCore Solutions. Not employment experience.';

export interface ReadinessGap {
  area: 'skills' | 'labs' | 'projects' | 'scenarios' | 'portfolio' | 'interview' | 'certification';
  label: string;
  detail: string;
  recommendation: { kind: string; id: string; title: string };
}

export interface CareerReadinessReview {
  careerId: CareerId;
  /** 0..1 share of readiness checks met. */
  score: number;
  checks: { id: string; label: string; met: boolean; detail: string }[];
  gaps: ReadinessGap[];
  strengths: string[];
  notice: string;
}
